import m from './m.js'
import { api, auth, toasts } from './api.js'
import { DONE, laneCards, laneOf } from './lanes.js'

export const repos = {
  list: null,
  load() {
    if (repos.list || repos.loading) return
    repos.loading = api('GET', '/api/repos')
      .then(list => (repos.list = list))
      .catch(err => {
        repos.list = []
        toasts.show(err.message)
      })
  },
}

/** The open detail view registers itself here so live updates can refresh it. */
export const detail = {
  current: null,
  // last loaded issues ("owner/repo#12" → item): reopening shows them at once while reloading
  cache: new Map(),
}

/** Last board per repo in localStorage, shown instantly on reload while the fresh one loads. */
const saved = {
  key: repo => `board:${repo.toLowerCase()}`,
  read(repo) {
    try {
      return JSON.parse(localStorage.getItem(saved.key(repo)))
    } catch {
      return null
    }
  },
  write(repo, data) {
    const json = JSON.stringify(data)
    try {
      localStorage.setItem(saved.key(repo), json)
    } catch {
      // quota: drop other boards and try once more
      try {
        Object.keys(localStorage)
          .filter(k => k.startsWith('board:'))
          .forEach(k => localStorage.removeItem(k))
        localStorage.setItem(saved.key(repo), json)
      } catch {
        // storage unavailable (private mode, blocked): works without it
      }
    }
  },
}

/** What a PATCH of `fields` changes on a card, to show it before GitHub answers. */
export function localChanges(fields, { labels, assignees, milestones }) {
  const changes = {}
  if (fields.title) changes.title = fields.title
  if (fields.body !== undefined)
    Object.assign(changes, { body: fields.body, bodyHTML: null })
  if (fields.labels)
    changes.labels = fields.labels.map(
      name => labels.find(l => l.name === name) || { name, color: 'ededed' }
    )
  if (fields.assignees)
    changes.assignees = fields.assignees.map(
      login => assignees.find(a => a.login === login) || { login }
    )
  if ('milestone' in fields)
    changes.milestone =
      milestones.find(ms => ms.number === fields.milestone) || null
  if (fields.state)
    Object.assign(changes, {
      state: fields.state,
      closedAt: fields.state === 'open' ? null : new Date().toISOString(),
    })
  return changes
}

/** Whether moving a card into the lane named `lane` assigns the mover when nobody is assigned. */
function selfAssigns(lane) {
  return board.data.config.lanes.some(l => l.name === lane && l.selfAssign)
}

export const board = {
  key: null,
  data: null,
  error: null,
  poller: null,
  live: false,
  timer: null,
  pending: new Set(),
  busy: new Map(), // card number → writes in flight
  settleTimers: {},
  unknownRetries: 0,

  open(key) {
    if (key === board.key) return
    board.poller?.abort()
    Object.assign(board, {
      key,
      data: saved.read(key),
      error: null,
      unknownRetries: 0,
      live: false,
    })
    try {
      localStorage.setItem('lastRepo', key)
    } catch {
      // storage unavailable (private mode, blocked): works without it
    }
    board.load()
    board.poll(key)
  },

  /** Long poll for changes (see server/events.js); runs until another repo is opened. */
  async poll(key) {
    const poller = new AbortController()
    board.poller = poller
    let since = ''
    let boot = ''
    while (!poller.signal.aborted) {
      try {
        const res = await fetch(
          `/api/repos/${key}/changes?since=${since}&boot=${boot}`,
          { signal: poller.signal }
        )
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()
        ;({ boot, seq: since } = data)
        board.setLive(true)
        data.changes.forEach(board.onEvent)
      } catch {
        if (poller.signal.aborted) return
        board.setLive(false)
        await new Promise(resolve => setTimeout(resolve, 3000))
      }
    }
  },

  setLive(live) {
    if (board.live === live) return
    board.live = live
    m.redraw()
  },

  /** Live updates: new card order, cards to refetch, a PR's head commit, or a full reload. */
  onEvent({ positions, numbers, sha, full }) {
    if (!board.data) return
    if (positions) {
      board.data.positions = positions
      return m.redraw()
    }
    if (sha)
      numbers = board.data.cards
        .filter(c => c.pr?.head === sha)
        .map(c => c.number)
    if (full) board.refreshSoon()
    else numbers?.forEach(n => board.pending.add(n))
    clearTimeout(board.pendingTimer)
    board.pendingTimer = setTimeout(board.refreshPending, 300)
    // GitHub sends the webhook before its API returns the change: reloaded after settle()'s delay
    const shown = detail.current?.number
    if (shown && (full || numbers?.includes(shown))) board.settle(shown)
  },

  async load() {
    const key = board.key
    board.loading = true
    try {
      const data = await api('GET', `/api/repos/${key}/board`)
      if (key !== board.key) return
      // the server sends PR status it has cached; the rest is fetched, showing the previous meanwhile
      const missing = data.cards
        .filter(c => c.pr && c.state === 'open' && !c.pr.status)
        .map(c => c.number)
      for (const card of data.cards)
        if (card.pr && !card.pr.status)
          card.pr.status = board.card(card.number)?.pr?.status ?? null
      board.data = data
      board.error = null
      board.save()
      board.loadStatus(missing)
    } catch (err) {
      if (key === board.key) board.error = err.message
    } finally {
      if (key === board.key) board.loading = false
    }
    m.redraw()
  },

  save() {
    clearTimeout(board.saveTimer)
    const { key, data } = board
    board.saveTimer = setTimeout(() => data && saved.write(key, data), 1000)
  },

  /** PR merge/check/review status is slow on GitHub's side, so it arrives after the board. */
  async loadStatus(numbers) {
    if (!numbers.length) return
    const key = board.key
    try {
      const statuses = await api(
        'GET',
        `/api/repos/${key}/pr-status?numbers=${numbers.join(',')}`
      )
      if (key !== board.key) return
      for (const [number, s] of Object.entries(statuses)) {
        const card = board.card(Number(number))
        if (card) card.pr.status = s
      }
      // GitHub computes mergeability lazily; ask again shortly
      const unknown = Object.entries(statuses)
        .filter(([, s]) => s.mergeState === 'UNKNOWN')
        .map(([n]) => Number(n))
      if (unknown.length && board.unknownRetries++ < 3)
        setTimeout(() => board.loadStatus(unknown), 4000)
      board.save()
      if (!unknown.length) board.unknownRetries = 0
    } catch (err) {
      toasts.show(m.t('pr.statusFailed', { error: err.message }))
    }
    m.redraw()
  },

  /** Refetches single cards (with PR status) after a live event. */
  async refreshPending() {
    // cards with writes in flight keep their optimistic state; settle() refetches them later
    const numbers = [...board.pending].filter(n => !board.busy.has(n))
    numbers.forEach(n => board.pending.delete(n))
    const key = board.key
    await Promise.all(
      numbers.map(async number => {
        try {
          const card = await api('GET', board.url(`/cards/${number}`))
          if (key !== board.key) return
          const cards = board.data.cards
          const i = cards.findIndex(c => c.number === number)
          if (i >= 0) cards[i] = card
          else cards.push(card)
        } catch {
          // the next full load brings it
        }
      })
    )
    board.save()
    m.redraw()
  },

  refreshSoon() {
    clearTimeout(board.timer)
    board.timer = setTimeout(board.load, 600)
  },

  card: number => board.data?.cards.find(c => c.number === number),
  url: path => `/api/repos/${board.key}${path}`,

  /**
   * Optimistic write for one card: the UI is already updated, this runs the request.
   * Once the card's last write is done, settle() fetches the real state from GitHub,
   * which also undoes the optimistic change if the write failed.
   */
  async track(number, promise) {
    board.busy.set(number, (board.busy.get(number) || 0) + 1)
    try {
      return await promise
    } catch (err) {
      toasts.show(err.message)
      throw err
    } finally {
      const left = board.busy.get(number) - 1
      if (left) board.busy.set(number, left)
      else {
        board.busy.delete(number)
        board.settle(number)
      }
      m.redraw()
    }
  },

  settle(number) {
    clearTimeout(board.settleTimers[number])
    // a short delay: GitHub's API can return the previous state right after a write
    board.settleTimers[number] = setTimeout(() => {
      if (board.busy.has(number)) return
      board.pending.add(number)
      board.refreshPending()
      if (detail.current?.number === number) detail.current.reload()
    }, 1500)
  },

  /** Card and open detail item of an issue, both updated optimistically. */
  targets: number =>
    [
      board.card(number),
      detail.current?.number === number && detail.current.item,
    ].filter(Boolean),

  /**
   * Moves a card into `lane` before card `before` (null = end of lane). `assign`: assign
   * the mover when nobody is assigned, as a self-assigning lane does.
   */
  async move(number, lane, before = null, { assign = false } = {}) {
    const card = board.card(number)
    const from = laneOf(card)
    const wasClosed = from === DONE

    if (lane === DONE) {
      if (wasClosed || card.type === 'pr') return
      return board.patch(number, { state: 'closed', state_reason: 'completed' })
    }
    if (wasClosed && card.type === 'pr') return

    const order = laneCards(board.data, lane)
      .filter(c => c !== card)
      .map(c => c.number)
    const at = before == null ? order.length : order.indexOf(before)
    order.splice(at < 0 ? order.length : at, 0, number)
    order.forEach((n, i) => (board.data.positions[n] = i))

    const calls = [api('PUT', board.url('/order'), { numbers: order })]
    if (from !== lane) {
      const changes = {
        lane,
        ...(wasClosed && { state: 'open', closedAt: null }),
        ...((assign || selfAssigns(lane)) &&
          card.assignees.length === 0 && { assignees: [auth.user] }),
      }
      for (const t of board.targets(number)) Object.assign(t, changes)
      // how the lane is kept (label or the board's database) is up to the server
      calls.push(
        api('PUT', board.url(`/cards/${number}/lane`), {
          lane,
          reopen: wasClosed,
          ...(assign && { assign }),
        })
      )
    }
    return board.track(number, Promise.all(calls))
  },

  /**
   * Starting Claude Code on an open issue moves it to the end of the lane configured as
   * `claude.lane` and assigns the starter when nobody is assigned.
   */
  startClaude(number) {
    const card = board.card(number)
    const lane = board.data.config.claude.lane
    if (!board.data.repo.canTriage || card?.type !== 'issue' || !lane) return
    if (laneOf(card) !== lane)
      board.move(number, lane, null, { assign: true }).catch(() => {})
    else if (card.assignees.length === 0)
      board.patch(number, { assignees: [auth.user.login] }).catch(() => {})
  },

  /** PATCHes issue fields (title, body, state, labels, assignees, milestone), optimistically. */
  patch(number, fields) {
    const changes = localChanges(fields, board.data)
    for (const t of board.targets(number)) Object.assign(t, changes)
    m.redraw()
    return board.track(
      number,
      api('PATCH', board.url(`/issues/${number}`), fields)
    )
  },

  /** Creates an issue in the lane named `lane`, shown at once; without one it lands in the default lane. Resolves to its number, or null. */
  async create(lane, title, body) {
    const card = {
      type: 'issue',
      number: -Date.now(),
      creating: true,
      title,
      url: null,
      state: 'open',
      createdAt: new Date().toISOString(),
      closedAt: null,
      author: null,
      lane: lane ?? board.data.config.unlabeled.issues,
      labels: [],
      assignees: selfAssigns(lane) ? [auth.user] : [],
      milestone: null,
      comments: 0,
      links: [],
      pr: null,
    }
    board.data.cards.push(card)
    m.redraw()
    try {
      const { number } = await api('POST', board.url('/issues'), {
        title,
        body,
        lane,
      })
      Object.assign(card, { number, creating: false })
      board.settle(number)
      return number
    } catch (err) {
      board.data.cards = board.data.cards.filter(c => c !== card)
      toasts.show(m.t('detail.createFailed', { title, error: err.message }))
      return null
    } finally {
      m.redraw()
    }
  },

  // not optimistic: a merge can be refused (conflicts, protection, a newer push), the
  // button shows progress; the refetch after a refusal brings the card's current state
  async merge(number, method, deleteBranch = false) {
    const pr = board.card(number)?.pr
    const branch = pr?.branch || ''
    const result = await board.track(
      number,
      api('POST', board.url(`/pulls/${number}/merge`), {
        method,
        deleteBranch,
        head: pr?.head,
      }).catch(err => {
        throw err.status === 409 ? new Error(m.t('merge.headMoved')) : err
      })
    )
    for (const t of board.targets(number)) {
      Object.assign(t, { state: 'merged', closedAt: new Date().toISOString() })
      if (result.branchDeleted) t.pr.branchExists = false
    }
    toasts.show(
      result.branchDeleted
        ? m.t('merge.doneDeleted', { number, branch })
        : m.t('merge.done', { number }),
      'success'
    )
    // merged all the same; the branch can still be deleted from the card
    if (result.deleteError)
      toasts.show(
        m.t('branch.deleteFailed', { branch, error: result.deleteError })
      )
  },

  // not optimistic: GitHub refuses it after a newer push; the webhook brings the new head
  async rebase(number) {
    const card = board.card(number)
    await board.track(
      number,
      api('POST', board.url(`/pulls/${number}/rebase`), { head: card?.pr.head })
    )
    toasts.show(
      m.t('rebase.done', { branch: card?.pr.branch, base: card?.pr.base }),
      'success'
    )
  },

  async deleteBranch(number) {
    const branch = board.card(number)?.pr.branch || ''
    for (const t of board.targets(number)) t.pr.branchExists = false
    await board.track(
      number,
      api('POST', board.url(`/pulls/${number}/delete-branch`))
    )
    toasts.show(m.t('branch.deleted', { branch }), 'success')
  },
}
